import { ChangeEvent, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';

import { ConsentHubClientConfigProvider } from '../../../../../analytics/consent-hub/consent-client';

import BillingLicensing from './billing-licensing';
import MigrationOptions from './migration-options';
import PricingQuotes from './pricing-quotes';
import ProductFeatures from './product-features';
import TechnicalSupport from './technical-support';

const wrapperStyle = css({
	display: 'flex',
	flexDirection: 'column',
	marginInlineStart: 'auto',
	marginInlineEnd: 'auto',
	marginBlockStart: '0px',
	paddingBlockStart: '79px',
});

const mainContainerStyle = css({
	position: 'relative',
	boxSizing: 'border-box',
	display: 'block',
	marginBlockEnd: '40px',
});

const rowStyle = css({
	marginInlineEnd: 'auto',
	marginInlineStart: 'auto',
	maxWidth: '1200px',
});

const formStyle = css({
	marginInlineStart: '20px',
	lineHeight: '1.33333333',
	fontSize: '2.2rem',
	width: '100%',
	fontFamily: 'Charlie Display, Arial,sans-serif',
	fontWeight: '500',
	letterSpacing: '0.02rem',
	verticalAlign: 'base-line',
	color: '#253858',
	[media.below.sm]: {
		boxSizing: 'border-box',
		display: 'block',
		width: `calc(100% - 35px)`,
	},
});

const selectorStyle = css({
	borderColor: 'rgba(0, 0, 0, 0)',
	borderWidth: '1px',
	boxSizing: 'border-box',
	fontFamily: 'Charlie Display, Arial,sans-serif',
	maxWidth: '500px',
	padding: '11px 56px 12px 12px',
	fontWeight: '400',
	fontSize: '2.25rem',
	lineHeight: '1.2',
	borderRadius: '5px',
	textAlign: 'left',
	alignItems: 'center',
	color: '#0052cc',
	backgroundColor: '#f4f5f7',
	textTransform: 'lowercase',
	WebkitAppearance: 'none',
	cursor: 'pointer',
	backgroundImage: `linear-gradient(45deg, transparent 40%, #253858 40% 60%, transparent 60%),linear-gradient(-45deg, transparent 40%, #253858 40% 60%, transparent 60%)`,
	backgroundSize: '8px 8px',
	backgroundPosition: 'right 28px center,right 22px center',
	backgroundRepeat: 'no-repeat',
	':hover': {
		backgroundColor: '#deebff',
	},

	[media.below.sm]: {
		marginInlineStart: '16px',
		maxWidth: `calc(100% - 35px)`,
	},
});

const ConstantSelector = () => {
	const intl = useIntl();

	const discussText = intl.formatMessage({
		id: 'constant-selector.main.discuss',
		defaultMessage: "I'd like to discuss ",
	});
	const productFeaturesText = intl.formatMessage({
		id: 'constant-selector.main.product-features',
		defaultMessage: 'product features',
	});
	const pricingQuotesText = intl.formatMessage({
		id: 'constant-selector.main.pricing-quotes',
		defaultMessage: 'pricing & quotes',
	});
	const migrationOptionsText = intl.formatMessage({
		id: 'constant-selector.main.migration-options',
		defaultMessage: 'migration options',
	});
	const billingLicensingText = intl.formatMessage({
		id: 'constant-selector.main.billing-licensing',
		defaultMessage: 'billing & licensing',
	});
	const technicalSupportText = intl.formatMessage({
		id: 'constant-selector.main.technical-support',
		defaultMessage: 'technical support',
	});

	const urlParams = new URLSearchParams(window.location.search);
	const formType = urlParams.get('formType');
	const formTypeOptions: { [x: string]: string } = {
		'product-features': 'product features',
		'pricing-quotes': 'pricing & quotes',
		'migration-options': 'migration options',
		'billing-licensing': 'billing & licensing',
		'technical-support': 'technical support',
	};
	const formTypeOption = formTypeOptions[formType || 'product-features'];
	const [selectedOption, setSelectedOption] = useState(formTypeOption);

	const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
		setSelectedOption(event.target.value);
		const newParams = new URLSearchParams(window.location.search);
		newParams.set('formType', event.target.value.replace(/&/g, '').replace(/\s+/g, '-'));
		window.history.pushState({}, '', `?${newParams.toString()}`);
	};

	return (
		<ConsentHubClientConfigProvider>
			<section css={wrapperStyle}>
				<div css={mainContainerStyle}>
					<div css={rowStyle}>
						<span css={formStyle}>{discussText}</span>
						<select value={selectedOption} onChange={handleChange} css={selectorStyle}>
							<option value="product features">{productFeaturesText}</option>
							<option value="pricing & quotes">{pricingQuotesText}</option>
							<option value="migration options">{migrationOptionsText}</option>
							<option value="billing & licensing">{billingLicensingText}</option>
							<option value="technical support">{technicalSupportText}</option>
						</select>
						{selectedOption === 'product features' && <ProductFeatures />}
						{selectedOption === 'pricing & quotes' && <PricingQuotes />}
						{selectedOption === 'migration options' && <MigrationOptions />}
						{selectedOption === 'billing & licensing' && <BillingLicensing />}
						{selectedOption === 'technical support' && <TechnicalSupport />}
					</div>
				</div>
			</section>
		</ConsentHubClientConfigProvider>
	);
};

export default ConstantSelector;
